/**
 * Tracking SDK loader + event helpers.
 *
 * Single source of truth on the client for:
 *   1. Loading GTM (if configured) OR direct pixels (GA4, Meta, TikTok)
 *   2. Pushing GA4-Enhanced-Ecommerce events to dataLayer
 *   3. Mirroring those events to fbq() / ttq() with the correct nomenclature
 *
 * Loader respects Google Consent Mode v2: scripts are always loaded, but
 * pixels self-respect the `consent` directives. The consent gate is
 * already installed by `bootstrapConsentMode()` at app boot.
 *
 * The brand admin can configure tracking via the AdminTracking page;
 * `/api/public/tracking/{brand_id}` returns the IDs that get injected.
 */

import axios from 'axios';
import { useEffect } from 'react';
import { getBackendUrl } from './origin';
import { loadConsent, onConsentChange } from './consent';

const API = getBackendUrl();

let _loaded = {};        // {brand_id: bool} → script injection idempotent
let _config = {};        // {brand_id: { gtm, ga4, meta, tiktok }}
let _eventsQueue = [];   // events fired before load() resolves

/* ── 1. Loader ────────────────────────────────────────────────────── */

/**
 * Fetch tracking config for the brand and inject the appropriate
 * scripts. Safe to call multiple times — idempotent per brand.
 *
 * @param {string} brandId
 * @returns {Promise<object>} config
 */
export async function loadTracking(brandId) {
  if (!brandId) return null;
  if (_loaded[brandId]) return _config[brandId];

  let cfg;
  try {
    const r = await axios.get(`${API}/api/public/tracking/${brandId}`);
    cfg = r.data || {};
  } catch {
    return null;
  }
  _config[brandId] = cfg;
  _loaded[brandId] = true;

  // GTM has priority — when configured it injects everything else
  if (cfg.gtm_container_id) {
    injectGtm(cfg.gtm_container_id);
  } else {
    if (cfg.ga4_measurement_id) injectGa4(cfg.ga4_measurement_id);
    if (cfg.meta_pixel_id) injectMetaPixel(cfg.meta_pixel_id);
    if (cfg.tiktok_pixel_id) injectTikTokPixel(cfg.tiktok_pixel_id);
  }

  // Flush events queued before SDK was ready
  while (_eventsQueue.length) {
    const e = _eventsQueue.shift();
    _dispatch(e.name, e.params, cfg);
  }
  return cfg;
}

function injectScript(src, attrs = {}) {
  if (typeof document === 'undefined') return;
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
}

function injectGtm(containerId) {
  if (window.__lazoGtmInjected) return;
  window.__lazoGtmInjected = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${containerId}`);
}

function injectGa4(measurementId) {
  if (window.__lazoGa4Injected) return;
  window.__lazoGa4Injected = true;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`);
  // gtag is already stubbed by bootstrapConsentMode()
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: true });
}

function injectMetaPixel(pixelId) {
  if (window.__lazoMetaInjected) return;
  window.__lazoMetaInjected = true;
  // Standard Meta Pixel snippet
  // eslint-disable-next-line
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}

function injectTikTokPixel(pixelId) {
  if (window.__lazoTikTokInjected) return;
  window.__lazoTikTokInjected = true;
  // Standard TikTok Pixel snippet
  // eslint-disable-next-line
  !function (w, d, t) { w.TiktokAnalyticsObject = t; var ttq = w[t] = w[t] || []; ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"], ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } }; for (var i = 0; i < ttq.methods.length; i++)ttq.setAndDefer(ttq, ttq.methods[i]); ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)ttq.setAndDefer(e, ttq.methods[n]); return e }, ttq.load = function (e, n) { var r = "https://analytics.tiktok.com/i18n/pixel/events.js", o = n && n.partner; ttq._i = ttq._i || {}, ttq._i[e] = [], ttq._i[e]._u = r, ttq._t = ttq._t || {}, ttq._t[e] = +new Date, ttq._o = ttq._o || {}, ttq._o[e] = n || {}; var i = document.createElement("script"); i.type = "text/javascript", i.async = !0, i.src = r + "?sdkid=" + e + "&lib=" + t; var a = document.getElementsByTagName("script")[0]; a.parentNode.insertBefore(i, a) }; ttq.load(pixelId); ttq.page(); }(window, document, 'ttq');
}

/* ── 2. Event mapping (GA4 ↔ Meta ↔ TikTok) ───────────────────────── */

const GA4_TO_META = {
  view_item: 'ViewContent',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'Purchase',
};
const GA4_TO_TIKTOK = {
  view_item: 'ViewContent',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'CompletePayment',
};

/**
 * Fire a tracked event. Automatically:
 *   - pushes to dataLayer (for GTM-based setups)
 *   - calls gtag('event', …) if GA4 is loaded directly
 *   - calls fbq('track', …) for Meta with the mapped event name
 *   - calls ttq.track(…) for TikTok with the mapped event name
 *
 * Marketing-class events are gated by the visitor's marketing consent;
 * statistics events (view_item) require statistics consent. Necessary
 * events always pass through.
 *
 * @param {string} brandId
 * @param {string} eventName  GA4 ecommerce event name (e.g. 'purchase')
 * @param {object} params     Ecommerce payload (currency, value, transaction_id, items[], event_id)
 */
export function trackEvent(brandId, eventName, params = {}) {
  if (!brandId || !eventName) return;
  // Queue events fired before loadTracking() resolves
  if (!_loaded[brandId]) {
    _eventsQueue.push({ name: eventName, params });
    return;
  }
  _dispatch(eventName, params, _config[brandId]);
}

function _dispatch(eventName, params, cfg) {
  // Consent gate
  const consent = loadConsent();
  if (!consent) return; // user hasn't decided yet — drop silently
  const isMarketing = ['purchase', 'add_payment_info', 'begin_checkout'].includes(eventName);
  const isStats = ['view_item'].includes(eventName) || isMarketing; // marketing also implies stats
  if (isStats && !consent.statistics && !consent.marketing) return;

  const payload = {
    event: eventName,            // dataLayer (GTM convention)
    ecommerce: {
      currency: params.currency || 'EUR',
      value: params.value || 0,
      transaction_id: params.transaction_id,
      items: params.items || [],
    },
    event_id: params.event_id || params.transaction_id, // for dedup with CAPI
  };

  // 1. dataLayer push (handles GTM + GA4-via-GTM)
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch { /* ignore */ }

  // 2. Direct GA4 gtag() call (when no GTM)
  if (!cfg?.gtm_container_id && cfg?.ga4_measurement_id && window.gtag) {
    try {
      window.gtag('event', eventName, {
        currency: payload.ecommerce.currency,
        value: payload.ecommerce.value,
        transaction_id: payload.ecommerce.transaction_id,
        items: payload.ecommerce.items,
      });
    } catch { /* ignore */ }
  }

  // 3. Meta — gated by marketing consent
  if (cfg?.meta_pixel_id && window.fbq && consent.marketing) {
    const metaName = GA4_TO_META[eventName];
    if (metaName) {
      try {
        window.fbq('track', metaName, {
          currency: payload.ecommerce.currency,
          value: payload.ecommerce.value,
          contents: (payload.ecommerce.items || []).map(it => ({
            id: it.item_id, quantity: it.quantity, item_price: it.price,
          })),
          content_type: 'product',
          content_ids: (payload.ecommerce.items || []).map(it => it.item_id),
        }, { eventID: payload.event_id });
      } catch { /* ignore */ }
    }
  }

  // 4. TikTok — gated by marketing consent
  if (cfg?.tiktok_pixel_id && window.ttq && consent.marketing) {
    const tiktokName = GA4_TO_TIKTOK[eventName];
    if (tiktokName) {
      try {
        window.ttq.track(tiktokName, {
          value: payload.ecommerce.value,
          currency: payload.ecommerce.currency,
          contents: (payload.ecommerce.items || []).map(it => ({
            content_id: it.item_id,
            content_name: it.item_name,
            quantity: it.quantity,
            price: it.price,
          })),
          event_id: payload.event_id,
        });
      } catch { /* ignore */ }
    }
  }
}

/* ── 3. Re-fire on consent grant — purchase events queued while denied
 * are NOT auto-fired (they'd be stale anyway). Only the CURRENT page
 * view is re-evaluated by GA4 internals via Consent Mode "wait_for_update".
 */
onConsentChange(() => { /* hook reserved for future use */ });

/* ── 4. React hook ────────────────────────────────────────────────── */

export function useTrackingBootstrap(brandId) {
  // Async loader — fire and forget on mount.
  useEffect(() => {
    if (!brandId) return;
    loadTracking(brandId);
  }, [brandId]);
}
